import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MedicationTemplate, AdditionalNotesTemplate, Medication } from "@/types";

interface TemplateViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template: MedicationTemplate | AdditionalNotesTemplate | null;
  type: "medication" | "additional-notes";
}

export function TemplateViewDialog({ isOpen, onClose, template, type }: TemplateViewDialogProps) {
  if (!template) return null;

  const isMedicationTemplate = type === "medication";
  const medicationTemplate = isMedicationTemplate ? (template as MedicationTemplate) : null;
  const additionalNotesTemplate = !isMedicationTemplate ? (template as AdditionalNotesTemplate) : null;

  let medications: Medication[] = [];
  if (medicationTemplate) {
    try {
      if (typeof medicationTemplate.medications === 'string') {
        medications = JSON.parse(medicationTemplate.medications);
      } else if (Array.isArray(medicationTemplate.medications)) {
        medications = medicationTemplate.medications;
      }
    } catch (e) {
      console.error('Error parsing medications:', e);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Template Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Template Name</label>
              <p className="text-base font-semibold mt-1">
                {isMedicationTemplate ? medicationTemplate.templateName : additionalNotesTemplate?.templateName || 'Untitled'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {isMedicationTemplate ? 'Diagnosis' : 'Disease'}
              </label>
              <div className="mt-1">
                <Badge variant="secondary">
                  {isMedicationTemplate ? medicationTemplate.diagnosis : additionalNotesTemplate?.disease || 'N/A'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Medication Details */}
          {isMedicationTemplate && medications.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Medications ({medications.length})
              </label>
              <div className="border rounded-md overflow-hidden max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Medication</TableHead>
                      <TableHead>Dosage</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Pattern</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Instructions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {medications.map((medication, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell className="font-medium">{medication.name}</TableCell>
                        <TableCell>{medication.dosage} {medication.doseType || ''}</TableCell>
                        <TableCell>{medication.frequency || 'N/A'}</TableCell>
                        <TableCell>{medication.frequencyPattern || '-'}</TableCell>
                        <TableCell>{medication.duration} {medication.durationUnit || 'days'}</TableCell>
                        <TableCell className="max-w-[200px]">{medication.instructions || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Additional Notes Details */}
          {!isMedicationTemplate && additionalNotesTemplate && (
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-3 block">
                Notes Content
              </label>
              <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
                <p className="whitespace-pre-wrap text-sm">
                  {additionalNotesTemplate.notes || 'No notes available'}
                </p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {isMedicationTemplate && medications.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No medications in this template</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

